import { ChevronDown, ChevronUp, CheckCircle2, Circle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "#app/utils/misc";

export type TocStep = { id: string; title: string; level: number };

export function TutorialToc({
  steps,
  currentStep,
  completedSteps,
  onStepChange,
}: {
  steps: TocStep[];
  currentStep: number;
  completedSteps: string[];
  onStepChange: (index: number) => void;
}) {
  const { t } = useTranslation("docs");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const completedCount = Math.min(new Set(completedSteps).size, steps.length);

  if (!steps || steps.length === 0) return null;

  const handleClick = (index: number) => {
    onStepChange(index);

    // Scroll to the step section smoothly
    setTimeout(() => {
      const stepId = steps[index]?.id;
      if (stepId) {
        const element = document.getElementById(stepId);
        if (element) {
          const mainContent = element.closest("main");
          if (mainContent) {
            const elementTop = element.offsetTop;
            mainContent.scrollTo({
              top: elementTop - 100,
              behavior: "smooth",
            });
          }
        }
      }
    }, 100);
  };

  return (
    <div className="space-y-1">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex items-center justify-between w-full text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 hover:text-gray-700 transition-colors dark:hover:text-gray-300"
      >
        <span className="flex items-center gap-2">
          <span>🎯</span>
          {t("tutorialSteps")}
        </span>
        {isCollapsed ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronUp className="h-3 w-3" />
        )}
      </button>

      {!isCollapsed && (
        <nav className="space-y-1">
          {steps.map((step, index) => {
            const isActive = currentStep === index;
            const isCompleted = completedSteps.includes(step.id);

            return (
              <button
                key={step.id}
                onClick={() => handleClick(index)}
                className={cn(
                  "w-full flex items-start gap-2 py-2 px-2 text-sm transition-all rounded-md text-left",
                  isActive
                    ? "relative text-[#535EA4] font-medium bg-[linear-gradient(90deg,#F0F4FF_0%,#FFFFFF_100%)] before:absolute before:left-0 before:top-1 before:bottom-1 before:w-1 before:rounded-full before:bg-[linear-gradient(180deg,#535EA4_0%,#909CCA_100%)] dark:bg-none dark:bg-zinc-800 dark:text-indigo-300"
                    : "text-gray-600 hover:text-[#141A42] hover:bg-gray-50 dark:text-zinc-400 dark:hover:bg-zinc-800",
                )}
              >
                {isCompleted ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500 dark:text-green-300 shrink-0 mt-0.5" />
                ) : (
                  <Circle className="h-4 w-4 shrink-0 mt-0.5" />
                )}
                <span className="line-clamp-2 flex-1">
                  {index + 1}. {step.title}
                </span>
              </button>
            );
          })}
        </nav>
      )}

      {!isCollapsed && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-zinc-700">
          <div className="text-xs text-gray-500">
            {t("completedCount", {
              completed: completedCount,
              total: steps.length,
            })}
          </div>
          <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-[linear-gradient(90deg,#5B66AA_0%,#909CCA_100%)] transition-all duration-300"
              style={{ width: `${(completedCount / steps.length) * 100}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
